import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useCallback } from 'react';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';
import { StaffMember } from '../api-types';
import { StaffMemberInternal } from '../types';
import { StringType } from '../../../../sv-strings';

export const createStaffMember = (
  member: StaffMember,
  strings: StringType
): StaffMemberInternal => {
  return {
    ...member,
    get fullName() {
      return this.displayName || `${this.givenName} ${this.surName}`;
    },
    get roleName() {
      return this.roleDisplayName || (this.role && strings.staffRoles[this.role]);
    },
  };
};

export const useStaff = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const addStaffMembersToTeam = useCallback(
    async (teamId: string, season: string, staff: StaffMember[]) => {
      try {
        const response = await adminApi.post({
          path: `/staff/${teamId}/${season}`,
          auth: await currentUser?.getIdToken(),
          body: {
            staff,
          },
        });

        if (!response.ok) {
          throw new Error(await response.json());
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const editStaffMembers = useCallback(
    async (teamId: string, season: string, staff: StaffMember[]) => {
      try {
        const response = await adminApi.put({
          path: `/staff/${teamId}/${season}`,
          auth: await currentUser?.getIdToken(),
          body: {
            staff,
          },
        });

        if (!response.ok) {
          throw new Error(await response.json());
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const removeStaffMemberFromTeam = useCallback(
    async (teamId: string, season: string, staff: StaffMember) => {
      try {
        const response = await adminApi.delete({
          path: `/staff/${teamId}/${season}/${staff.clId}`,
          auth: await currentUser?.getIdToken(),
        });

        if (!response.ok) {
          throw new Error(await response.json());
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const fetchStaff = useCallback(
    async (teamId: string, season: string): Promise<StaffMemberInternal[] | null> => {
      try {
        const response = await adminApi.get({
          path: `/staff/${teamId}/${season}`,
          auth: await currentUser?.getIdToken(),
        });

        if (!response.ok) {
          throw new Error(`Fetching squad failed! (${response.status})`);
        }

        const staff: StaffMember[] = await response.json();
        return staff.map((member) => createStaffMember(member, strings));
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });

        throw error;
      }
    },
    [enqueueSnackbar, strings]
  );

  const uploadStaffMemberImage = useCallback(
    async (teamId: string, season: string, staffId: number, file: File) => {
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await adminApi.post({
          path: `/staff/${teamId}/${season}/${staffId}/image`,
          formData,
          auth: await currentUser?.getIdToken(),
        });

        if (!response.ok) {
          throw new Error(`Update staff failed! (${response.status})`);
        }

        enqueueSnackbar(strings.messages.saveSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  return {
    fetchStaff,
    addStaffMembersToTeam,
    removeStaffMemberFromTeam,
    editStaffMembers,
    uploadStaffMemberImage,
  };
};
